import React, { useState, useEffect } from "react";
import { X, Clock, MessageSquare, FileText, User, Paperclip, Send, PauseCircle, RotateCcw, Link } from "lucide-react";
import { toast } from "../../../ui/Toast";

const API = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";
const inputCls = "w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls = "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";

const PRI_COLOR = { P1:"text-red-400", P2:"text-orange-400", P3:"text-yellow-400", P4:"text-blue-400" };
const STATUS_STYLE = {
  "Open":        "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/20",
  "Pending":     "text-yellow-400 bg-yellow-500/10 border-yellow-500/20",
  "In Progress": "text-blue-400 bg-blue-500/10 border-blue-500/20",
  "Escalated":   "text-orange-400 bg-orange-500/10 border-orange-500/20",
  "On Hold":     "text-purple-400 bg-purple-500/10 border-purple-500/20",
  "Closed":      "text-white/25 bg-white/5 border-white/10",
};

const CANNED = [
  "We have received your request and are looking into it.",
  "This requires approval from the relevant team. We'll keep you posted.",
  "Your issue has been resolved. Please let us know if you need further help.",
  "We need more information to proceed. Could you please provide more details?",
  "This has been escalated to our L2 support team.",
  "We are currently waiting on a third party to respond. We'll update you shortly.",
  "Your ticket has been placed on hold pending further information.",
];

const TABS = [
  { key: "details",   label: "Details",   icon: MessageSquare },
  { key: "reply",     label: "Reply",     icon: Send          },
  { key: "timeline",  label: "Timeline",  icon: Clock         },
  { key: "requester", label: "Requester", icon: User          },
  { key: "canned",    label: "Canned",    icon: FileText      },
];

export default function TicketDrawer({
  open, ticket, onClose,
  onAssign, onSetStatus, onSetPriority, onAddNote, onApprove,
  onHold, onReopen, assignees = []
}) {
  const [tab,           setTab]           = useState("details");
  const [assignee,      setAssignee]      = useState("");
  const [status,        setStatus]        = useState("Open");
  const [priority,      setPriority]      = useState("P3");
  const [note,          setNote]          = useState("");
  const [reply,         setReply]         = useState("");
  const [ccInput,       setCcInput]       = useState("");
  const [ccList,        setCcList]        = useState([]);
  const [timeline,      setTimeline]      = useState([]);
  const [reqHistory,    setReqHistory]    = useState([]);
  const [linkedTickets, setLinkedTickets] = useState([]);
  const [linkInput,     setLinkInput]     = useState("");
  const [files,         setFiles]         = useState([]);
  const [holdReason,    setHoldReason]    = useState("");
  const [holdUntil,     setHoldUntil]     = useState("");
  const [slaExcuse,     setSlaExcuse]     = useState("");
  const [saving,        setSaving]        = useState(false);
  const [replySending,  setReplySending]  = useState(false);
  const [msg,           setMsg]           = useState(null);

  const ASSIGNEES = assignees.length > 0 ? assignees : ["HR Payroll","HR Ops","IT Help","IT L2"];

  useEffect(() => {
    if (ticket) {
      setAssignee(ticket.assignee || "");
      setStatus(ticket.status || "Open");
      setPriority(ticket.priority || "P3");
      setNote(""); setReply(""); setMsg(null); setFiles([]);
      setCcList(ticket.cc || []);
    }
  }, [ticket?.id]);

  useEffect(() => {
    if (!open || !ticket?.id) return;
    const tok = localStorage.getItem("token");
    const h   = { Authorization: `Bearer ${tok}` };
    Promise.all([
      fetch(`${API}/api/admin/tickets/${ticket.id}/timeline`, { headers: h }).then(r => r.json()).catch(() => []),
      fetch(`${API}/api/admin/tickets/requester/${encodeURIComponent(ticket.requester || "")}`, { headers: h }).then(r => r.json()).catch(() => []),
      fetch(`${API}/api/admin/tickets/${ticket.id}/linked`, { headers: h }).then(r => r.json()).catch(() => []),
    ]).then(([tl, rh, lk]) => {
      setTimeline(Array.isArray(tl) ? tl : []);
      setReqHistory(Array.isArray(rh) ? rh : []);
      setLinkedTickets(Array.isArray(lk) ? lk : []);
    });
  }, [open, ticket?.id]);

  const tok  = () => localStorage.getItem("token");
  const authH = () => ({ Authorization: `Bearer ${tok()}`, "Content-Type": "application/json" });

  const saveDetails = async () => {
    setSaving(true); setMsg(null);
    let ccChanged = false;
    let noteAdded = false;
    try {
      onAssign?.(ticket.id, assignee);
      onSetStatus?.(ticket.id, status);
      onSetPriority?.(ticket.id, priority);
      const ccRes = await fetch(`${API}/api/admin/tickets/${ticket.id}`, {
        method: "PATCH", headers: authH(), body: JSON.stringify({ cc: ccList }),
      });
      if (!ccRes.ok) throw new Error(`HTTP ${ccRes.status}`);
      ccChanged = true;
      if (note) {
        const noteRes = await fetch(`${API}/api/admin/tickets/${ticket.id}/notes`, {
          method: "POST", headers: authH(), body: JSON.stringify({ note, internal: true }),
        });
        if (!noteRes.ok) throw new Error(`HTTP ${noteRes.status}`);
        // Don't call onAddNote? — parent handler re-POSTs the same note.
        setNote("");
        noteAdded = true;
      }
      if (files.length > 0) {
        const fd = new FormData();
        files.forEach(f => fd.append("files", f));
        await fetch(`${API}/api/admin/tickets/${ticket.id}/attachments`, {
          method: "POST", headers: { Authorization: `Bearer ${tok()}` }, body: fd,
        });
        setFiles([]);
      }
      setMsg({ type: "success", text: "✓ Saved." });
      if (noteAdded) toast.success("Note added");
      if (ccChanged) toast.success("CC list updated");
    } catch (e) {
      setMsg({ type: "error", text: `⚠ ${e.message}` });
      if (!ccChanged)      toast.error("Failed to update CC");
      else if (note)       toast.error("Failed to add note");
    }
    finally { setSaving(false); }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setReplySending(true); setMsg(null);
    try {
      await fetch(`${API}/api/admin/tickets/${ticket.id}/reply`, {
        method: "POST", headers: authH(), body: JSON.stringify({ message: reply, cc: ccList }),
      });
      if (files.length > 0) {
        const fd = new FormData();
        files.forEach(f => fd.append("files", f));
        await fetch(`${API}/api/admin/tickets/${ticket.id}/attachments`, {
          method: "POST", headers: { Authorization: `Bearer ${tok()}` }, body: fd,
        });
        setFiles([]);
      }
      setReply("");
      setMsg({ type: "success", text: "✓ Reply sent to requester." });
      fetch(`${API}/api/admin/tickets/${ticket.id}/timeline`, { headers: { Authorization: `Bearer ${tok()}` } })
        .then(r => r.json()).then(d => setTimeline(Array.isArray(d) ? d : [])).catch(() => {});
    } catch (e) { setMsg({ type: "error", text: `⚠ ${e.message}` }); }
    finally { setReplySending(false); }
  };

  const holdTicket = async () => {
    if (!holdReason.trim()) return;
    try {
      const res = await fetch(`${API}/api/admin/tickets/${ticket.id}/hold`, {
        method: "POST", headers: authH(), body: JSON.stringify({ reason: holdReason, until: holdUntil || null }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // Notify parent for local-state propagation (status pill in the list).
      // Parent should treat onHold as a no-fetch hook to avoid duplicate POSTs.
      onHold?.(ticket.id, holdReason, holdUntil);
      setMsg({ type: "success", text: "✓ Ticket placed on hold." });
      setHoldReason(""); setHoldUntil("");
      toast.success("Ticket on hold");
    } catch (e) {
      setMsg({ type: "error", text: `⚠ ${e.message}` });
      toast.error("Failed to hold ticket");
    }
  };

  const reopenTicket = async () => {
    try {
      const res = await fetch(`${API}/api/admin/tickets/${ticket.id}/reopen`, { method: "POST", headers: authH() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // Notify parent for local-state propagation.
      onReopen?.(ticket.id);
      setMsg({ type: "success", text: "✓ Ticket reopened." });
      toast.success("Ticket reopened");
    } catch (e) {
      setMsg({ type: "error", text: `⚠ ${e.message}` });
      toast.error("Failed to reopen");
    }
  };

  const excuseSla = async () => {
    if (!slaExcuse.trim()) return;
    try {
      const res = await fetch(`${API}/api/admin/tickets/${ticket.id}/sla-exception`, {
        method: "POST", headers: authH(), body: JSON.stringify({ reason: slaExcuse }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSlaExcuse("");
      setMsg({ type: "success", text: "✓ SLA breach marked as excused." });
      toast.success("SLA excused");
    } catch (e) {
      setMsg({ type: "error", text: `⚠ ${e.message}` });
      toast.error("Failed to excuse SLA");
    }
  };

  const addCc = () => {
    const v = ccInput.trim();
    if (v && !ccList.includes(v)) { setCcList(c => [...c, v]); setCcInput(""); }
  };

  const linkTicket = async () => {
    const v = linkInput.trim();
    if (!v) return;
    try {
      const res = await fetch(`${API}/api/admin/tickets/${ticket.id}/linked`, {
        method: "POST", headers: authH(), body: JSON.stringify({ linked_id: v }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setLinkedTickets(l => [...l, { id: v, subject: "", status: "Open" }]);
      setLinkInput("");
      toast.success("Tickets linked");
    } catch (e) {
      setMsg({ type: "error", text: `⚠ ${e.message}` });
      toast.error("Failed to link");
    }
  };

  const isClosed = ticket?.status === "Closed";
  const isOnHold = ticket?.status === "On Hold";
  const statusStyle = STATUS_STYLE[ticket?.status] || STATUS_STYLE.Open;

  return (
    <div className="fixed inset-0 z-[10070]" style={{ display: open && ticket ? "block" : "none" }}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      <div className="absolute right-0 top-0 h-full w-full sm:w-[560px] bg-[#0d0d0d] border-l border-[#00ff90]/20 shadow-[-20px_0_60px_#00ff9011] text-white flex flex-col">
        {ticket && <>

          {/* Header */}
          <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-white/5 shrink-0">
            <div className="min-w-0">
              <p className="font-semibold text-white leading-snug">{ticket.subject}</p>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className="text-xs text-white/30 font-mono">{ticket.id}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full border ${statusStyle}`}>{ticket.status}</span>
                <span className={`text-xs font-bold ${PRI_COLOR[ticket.priority] || "text-white/40"}`}>{ticket.priority}</span>
                {ticket.breached && <span className="text-xs text-red-400 border border-red-500/30 bg-red-500/10 px-2 py-0.5 rounded-full">SLA Breached</span>}
              </div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              {(isClosed || isOnHold) && (
                <button onClick={reopenTicket}
                  className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">
                  <RotateCcw size={11} /> {isOnHold ? "Resume" : "Reopen"}
                </button>
              )}
              <button onClick={onClose}
                className="w-8 h-8 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/40 hover:text-white transition-colors">
                <X size={14} />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 px-4 py-2 border-b border-white/5 shrink-0 overflow-x-auto">
            {TABS.map(({ key, label, icon: Icon }) => (
              <button key={key} onClick={() => setTab(key)}
                className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors whitespace-nowrap ${
                  tab === key ? "bg-[#00ff90]/15 border-[#00ff90]/40 text-[#00ff90]" : "bg-transparent border-white/10 text-white/40 hover:border-[#00ff90]/20 hover:text-white/60"
                }`}>
                <Icon size={11} />{label}
              </button>
            ))}
          </div>

          {msg && (
            <div className={`mx-5 mt-3 rounded-lg px-4 py-2.5 text-xs border ${msg.type === "error" ? "border-red-500/30 bg-red-500/10 text-red-300" : "border-[#00ff90]/30 bg-[#00ff90]/10 text-[#00ff90]"}`}>
              {msg.text}
            </div>
          )}

          <div className="flex-1 overflow-y-auto p-5 space-y-4">

            {/* ── DETAILS TAB ── */}
            {tab === "details" && (
              <>
                <div className="rounded-xl border border-white/5 bg-white/3 divide-y divide-white/5">
                  {[["Category",ticket.category],["Requester",ticket.requester],["Department",ticket.dept],["Created",ticket.created?.replace("T"," ")],["Due",ticket.due?.replace("T"," ")]].map(([k,v]) => (
                    <div key={k} className="flex items-center justify-between px-4 py-2.5">
                      <span className="text-xs text-[#00ff90]/50 uppercase tracking-wider">{k}</span>
                      <span className="text-sm text-white/70">{v||"—"}</span>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div><label className={labelCls}>Assignee</label>
                    <select className={inputCls} value={assignee} onChange={e => setAssignee(e.target.value)}>
                      <option value="">Unassigned</option>
                      {ASSIGNEES.map(a => <option key={a}>{a}</option>)}
                    </select></div>
                  <div><label className={labelCls}>Status</label>
                    <select className={inputCls} value={status} onChange={e => setStatus(e.target.value)}>
                      {["Open","Pending","In Progress","Escalated","On Hold","Closed"].map(s => <option key={s}>{s}</option>)}
                    </select></div>
                  <div><label className={labelCls}>Priority</label>
                    <select className={inputCls} value={priority} onChange={e => setPriority(e.target.value)}>
                      {["P1","P2","P3","P4"].map(p => <option key={p}>{p}</option>)}
                    </select></div>
                </div>

                {/* CC */}
                <div>
                  <label className={labelCls}>CC Stakeholders</label>
                  <div className="flex gap-2 mb-2">
                    <input className={inputCls} value={ccInput} onChange={e => setCcInput(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && addCc()} placeholder="email@company.com" />
                    <button onClick={addCc} className="text-xs px-3 py-2 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors shrink-0">Add</button>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {ccList.map(cc => (
                      <span key={cc} className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border border-white/10 bg-white/5 text-white/50">
                        {cc} <button onClick={() => setCcList(c => c.filter(x => x !== cc))} className="text-white/30 hover:text-red-400 transition-colors ml-0.5"><X size={9} /></button>
                      </span>
                    ))}
                  </div>
                </div>

                {/* Pending approvals */}
                {(ticket.approvals||[]).length > 0 && (
                  <div className="rounded-xl border border-yellow-500/20 bg-yellow-500/5 p-4">
                    <p className="text-xs text-yellow-400 uppercase tracking-wider mb-2">Approvals Pending</p>
                    <div className="flex flex-wrap gap-2">
                      {ticket.approvals.map(a => (
                        <button key={a} onClick={() => onApprove?.(ticket.id, a)}
                          className="text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">
                          ✓ Approve: {a}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Internal note + attach */}
                <div>
                  <label className={labelCls}>Internal Note</label>
                  <textarea value={note} onChange={e => setNote(e.target.value)} rows={3}
                    className="w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 resize-none"
                    placeholder="Internal note — not visible to requester…" />
                  <label className="flex items-center gap-2 mt-2 cursor-pointer text-xs text-white/40 hover:text-white/60 transition-colors">
                    <Paperclip size={13} />
                    <span>Attach file(s)</span>
                    <input type="file" multiple className="hidden" onChange={e => setFiles(Array.from(e.target.files))} />
                    {files.length > 0 && <span className="text-[#00ff90]/60">{files.length} file(s)</span>}
                  </label>
                </div>

                {/* Hold */}
                {!isClosed && !isOnHold && (
                  <div className="rounded-xl border border-purple-500/20 bg-purple-500/5 p-4 space-y-3">
                    <p className="text-xs text-purple-400 uppercase tracking-wider">Place on Hold</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div><label className={labelCls}>Reason</label>
                        <input className={inputCls} value={holdReason} onChange={e => setHoldReason(e.target.value)} placeholder="Waiting for vendor…" /></div>
                      <div><label className={labelCls}>Auto-resume at (optional)</label>
                        <input type="datetime-local" className={inputCls} value={holdUntil} onChange={e => setHoldUntil(e.target.value)} /></div>
                    </div>
                    <button onClick={holdTicket} disabled={!holdReason}
                      className="flex items-center gap-1.5 text-xs px-4 py-2 rounded-lg border border-purple-500/30 bg-purple-500/10 text-purple-400 hover:bg-purple-500/20 disabled:opacity-40 transition-colors">
                      <PauseCircle size={12} /> Place on Hold
                    </button>
                  </div>
                )}

                {/* SLA exception */}
                {ticket.breached && (
                  <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-4 space-y-3">
                    <p className="text-xs text-red-400 uppercase tracking-wider">Excuse SLA Breach</p>
                    <input className={inputCls} value={slaExcuse} onChange={e => setSlaExcuse(e.target.value)} placeholder="Reason for excusing this breach…" />
                    <button onClick={excuseSla} disabled={!slaExcuse}
                      className="text-xs px-4 py-2 rounded-lg border border-red-500/30 bg-red-500/10 text-red-400 hover:bg-red-500/20 disabled:opacity-40 transition-colors">
                      Mark as Excused
                    </button>
                  </div>
                )}

                {/* Linked tickets */}
                <div>
                  <label className={labelCls}>Linked Tickets</label>
                  {linkedTickets.length === 0
                    ? <p className="text-white/20 text-xs italic mb-2">No linked tickets.</p>
                    : linkedTickets.map(l => (
                      <div key={l.id} className="flex items-center gap-2 text-xs rounded-lg border border-white/5 bg-white/3 px-3 py-2 mb-1.5">
                        <Link size={11} className="text-[#00ff90]/40 shrink-0" />
                        <span className="font-mono text-white/50">{l.id}</span>
                        <span className="text-white/30 truncate">{l.subject}</span>
                        {l.status && <span className={`ml-auto text-xs px-2 py-0.5 rounded-full border shrink-0 ${STATUS_STYLE[l.status] || STATUS_STYLE.Open}`}>{l.status}</span>}
                      </div>
                    ))
                  }
                  <div className="flex gap-2">
                    <input className={`${inputCls} flex-1`} value={linkInput} onChange={e => setLinkInput(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && linkTicket()} placeholder="Ticket ID to link…" />
                    <button onClick={linkTicket} className="text-xs px-3 py-2 rounded-lg border border-[#00ff90]/20 bg-[#00ff90]/5 text-[#00ff90]/60 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors shrink-0">Link</button>
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  <button onClick={saveDetails} disabled={saving}
                    className="text-xs px-5 py-2 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors font-medium">
                    {saving ? "Saving…" : "Save Changes"}
                  </button>
                  <button onClick={() => onApprove?.(ticket.id, "ADMIN_OVERRIDE")}
                    className="text-xs px-4 py-2 rounded-lg border border-purple-500/25 bg-purple-500/8 text-purple-400/70 hover:bg-purple-500/15 hover:text-purple-400 transition-colors">
                    Admin Override
                  </button>
                </div>
              </>
            )}

            {/* ── REPLY TAB ── */}
            {tab === "reply" && (
              <div className="space-y-4">
                <div className="rounded-xl border border-white/5 bg-white/3 divide-y divide-white/5">
                  <div className="flex items-center justify-between px-4 py-2.5">
                    <span className="text-xs text-[#00ff90]/50 uppercase tracking-wider">To</span>
                    <span className="text-sm text-white/70">{ticket.requester}</span>
                  </div>
                  {ccList.length > 0 && (
                    <div className="flex items-center justify-between px-4 py-2.5">
                      <span className="text-xs text-[#00ff90]/50 uppercase tracking-wider">CC</span>
                      <span className="text-sm text-white/50">{ccList.join(", ")}</span>
                    </div>
                  )}
                </div>
                <div>
                  <label className={labelCls}>Message</label>
                  <textarea value={reply} onChange={e => setReply(e.target.value)} rows={8}
                    className="w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 resize-none"
                    placeholder="Type your reply to the requester…" />
                </div>
                <label className="flex items-center gap-2 cursor-pointer text-xs text-white/40 hover:text-white/60 transition-colors">
                  <Paperclip size={13} />
                  <span>Attach file(s)</span>
                  <input type="file" multiple className="hidden" onChange={e => setFiles(Array.from(e.target.files))} />
                  {files.length > 0 && <span className="text-[#00ff90]/60">{files.length} file(s)</span>}
                </label>
                <div className="flex gap-2">
                  <button onClick={sendReply} disabled={replySending || !reply.trim()}
                    className="flex items-center gap-1.5 text-xs px-5 py-2 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors font-medium">
                    <Send size={12} /> {replySending ? "Sending…" : "Send Reply"}
                  </button>
                  <button onClick={() => { setReply(CANNED[0]); }}
                    className="text-xs px-4 py-2 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors">
                    Use Canned
                  </button>
                </div>
                <p className="text-xs text-white/20">Sent via email and visible in the employee portal.</p>
              </div>
            )}

            {/* ── TIMELINE TAB ── */}
            {tab === "timeline" && (
              <div className="space-y-1">
                {timeline.length === 0 ? (
                  <p className="text-white/20 text-sm italic text-center py-6">No activity recorded yet.</p>
                ) : timeline.map((e, i) => {
                  const isReply = e.type === "reply";
                  const isNote  = e.type === "note";
                  return (
                    <div key={i} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${isReply ? "bg-blue-400/60" : isNote ? "bg-yellow-400/60" : "bg-[#00ff90]/40"}`} />
                        {i < timeline.length - 1 && <div className="w-px flex-1 bg-white/5 mt-1" />}
                      </div>
                      <div className="pb-3 min-w-0 flex-1">
                        <div className={`rounded-lg border px-3 py-2 mb-0.5 ${isReply ? "border-blue-500/15 bg-blue-500/5" : isNote ? "border-yellow-500/15 bg-yellow-500/5" : "border-white/5 bg-white/3"}`}>
                          <p className="text-sm text-white/70">{e.message || e.action}</p>
                        </div>
                        <p className="text-xs text-white/25">{e.actor} · {e.timestamp?.replace("T"," ")}{e.type ? ` · ${e.type}` : ""}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── REQUESTER HISTORY TAB ── */}
            {tab === "requester" && (
              <div className="space-y-3">
                <div className="rounded-xl border border-white/5 bg-white/3 p-4">
                  <p className="text-sm font-medium text-white/80">{ticket.requester}</p>
                  <p className="text-xs text-white/30 mt-0.5">{ticket.dept}</p>
                </div>
                <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider">All Past Tickets</p>
                {reqHistory.length === 0 ? (
                  <p className="text-white/20 text-sm italic text-center py-4">No previous tickets.</p>
                ) : reqHistory.map(t => (
                  <div key={t.id} className="rounded-lg border border-white/5 bg-white/3 px-4 py-3">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <p className="text-sm text-white/70 font-medium">{t.subject}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full border shrink-0 ${STATUS_STYLE[t.status] || STATUS_STYLE.Open}`}>{t.status}</span>
                    </div>
                    <p className="text-xs text-white/30">{t.id} · {t.category} · {t.created?.split("T")[0]}</p>
                  </div>
                ))}
              </div>
            )}

            {/* ── CANNED RESPONSES TAB ── */}
            {tab === "canned" && (
              <div className="space-y-2">
                <p className="text-xs text-white/30 mb-3">Click to copy to the reply field.</p>
                {CANNED.map((r, i) => (
                  <button key={i} onClick={() => { setReply(r); setTab("reply"); }}
                    className="w-full text-left rounded-lg border border-white/5 bg-white/3 px-4 py-3 text-sm text-white/60 hover:border-[#00ff90]/20 hover:text-white/80 hover:bg-[#00ff90]/5 transition-all">
                    {r}
                  </button>
                ))}
              </div>
            )}

          </div>
        </>}
      </div>
    </div>
  );
}